import * as React from 'react'

export type ColorSchemePref = 'light' | 'dark' | 'system'
const KEY = 'turf.admin.color-scheme'

function read(): ColorSchemePref {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system'
  } catch {
    return 'system'
  }
}

/** Admin-only colour scheme (the public website follows its CMS theme). */
export function useAdminColorScheme(enabled: boolean) {
  const [pref, setPref] = React.useState<ColorSchemePref>(read)

  React.useEffect(() => {
    const root = document.documentElement
    if (!enabled) {
      root.classList.remove('dark')
      return
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const apply = () => root.classList.toggle('dark', pref === 'dark' || (pref === 'system' && media.matches))
    apply()
    media.addEventListener('change', apply)
    return () => {
      media.removeEventListener('change', apply)
      root.classList.remove('dark')
    }
  }, [pref, enabled])

  const update = React.useCallback((next: ColorSchemePref) => {
    setPref(next)
    try {
      localStorage.setItem(KEY, next)
    } catch {
      /* storage unavailable: preference lasts for this visit only */
    }
  }, [])

  return [pref, update] as const
}

export function usePersistentState<T>(key: string, initial: T) {
  const [value, setValue] = React.useState<T>(() => {
    try {
      const raw = localStorage.getItem(key)
      return raw == null ? initial : (JSON.parse(raw) as T)
    } catch {
      return initial
    }
  })
  const update = React.useCallback(
    (next: T) => {
      setValue(next)
      try {
        localStorage.setItem(key, JSON.stringify(next))
      } catch {
        /* ignore */
      }
    },
    [key],
  )
  return [value, update] as const
}
